import React from 'react'
import styled from 'styled-components'

import CantileverRow from './CantileverRow'

interface CantileverCardProps {
  children: React.ReactNode
}

// Define an interface that describes the structure we want
interface CantileverCardComponent
  extends React.ForwardRefExoticComponent<
    CantileverCardProps & React.RefAttributes<HTMLDivElement>
  > {
  Section: React.FC<{
    children: React.ReactNode
    topBorder?: boolean
    bottomBorder?: boolean
    topPadding?: boolean
    bottomPadding?: boolean
    style?: React.CSSProperties
  }>
  Group: React.FC<{
    children: React.ReactNode
  }>
  HiddenUnlessHovering: React.FC<{
    children: React.ReactNode
  }>
  CardHeader: React.FC<{
    leadingContent: React.ReactNode | string
    trailingContent?: React.ReactNode
    subContent?: React.ReactNode | string
    sticky?: boolean
    style?: React.CSSProperties
  }>
  SectionHeader: React.FC<{
    leadingContent: React.ReactNode | string
    middleContent?: React.ReactNode
    trailingContent?: React.ReactNode
  }>
}

const HiddenUnlessHoveringContainer = styled.div`
  opacity: 0;
  display: flex;
  gap: 0.25rem;
`

const CantileverCardHiddenUnlessHoveringContainer: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  return (
    <HiddenUnlessHoveringContainer>{children}</HiddenUnlessHoveringContainer>
  )
}

const CardGroup = styled.div`
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 0.375rem;
  overflow: hidden;
`
const CantileverCardGroup: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  return <CardGroup>{children}</CardGroup>
}

const Card = styled.div`
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.5rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};

  &:hover {
    ${HiddenUnlessHoveringContainer} {
      opacity: 1;
    }
  }

  ${CardGroup}:last-of-type {
    margin-bottom: 0.75rem;
  }
`

const CantileverCard = React.forwardRef<
  HTMLDivElement,
  CantileverCardProps & { children: React.ReactNode }
>(({ children }, ref) => {
  return <Card ref={ref}>{children}</Card>
}) as CantileverCardComponent

CantileverCard.displayName = 'CantileverCard'

const CardSection = styled.div<{
  $topBorder?: boolean
  $bottomBorder?: boolean
  $topPadding?: boolean
  $bottomPadding?: boolean
}>`
  padding: 0 0.75rem;
  padding-top: ${({ $topPadding }) => ($topPadding ? '0.75rem' : 0)};
  padding-bottom: ${({ $bottomPadding }) => ($bottomPadding ? '0.75rem' : 0)};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-width: 0;
  border-top-width: ${({ $topBorder }) => ($topBorder ? '1px' : '0')};
  border-bottom-width: ${({ $bottomBorder }) => ($bottomBorder ? '1px' : '0')};
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.5rem 0.5rem 0 0;
`

export const CantileverCardSection: React.FC<{
  children: React.ReactNode
  topBorder?: boolean
  bottomBorder?: boolean
  topPadding?: boolean
  bottomPadding?: boolean
  style?: React.CSSProperties
}> = ({
  children,
  topBorder,
  bottomBorder,
  topPadding,
  bottomPadding,
  style,
}) => {
  return (
    <CardSection
      style={style}
      $topBorder={topBorder}
      $bottomBorder={bottomBorder}
      $topPadding={topPadding}
      $bottomPadding={bottomPadding}
      data-testid={'cantilever-card-section'}>
      {children}
    </CardSection>
  )
}

export const CardTitle = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  cursor: default;
`

const CardSubtitle = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  cursor: default;
`

const CantileverCardHeader: React.FC<{
  leadingContent: React.ReactNode | string
  trailingContent?: React.ReactNode
  subContent?: React.ReactNode | string
  sticky?: boolean
  style?: React.CSSProperties
}> = ({ leadingContent, trailingContent, subContent, sticky, style }) => {
  return (
    <CantileverCardSection
      style={{
        ...style,
        position: sticky ? 'sticky' : undefined,
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 1 : undefined,
      }}
      bottomBorder
      bottomPadding={subContent ? true : false}>
      <CantileverRow
        vPadding='0.5rem'
        tPadding={subContent ? '1rem' : undefined}
        bPadding={subContent ? '0rem' : undefined}
        leadingContent={<CardTitle>{leadingContent}</CardTitle>}
        trailingContent={trailingContent}
        contentHeight={subContent ? '0rem' : undefined}
      />
      <CardSubtitle>{subContent}</CardSubtitle>
    </CantileverCardSection>
  )
}

const SectionTitle = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
`

const CantileverCardSectionHeader: React.FC<{
  leadingContent?: React.ReactNode
  middleContent?: React.ReactNode
  trailingContent?: React.ReactNode
}> = ({ leadingContent, middleContent, trailingContent }) => {
  return (
    <CantileverRow
      vPadding='0.5rem'
      leadingContent={<SectionTitle>{leadingContent}</SectionTitle>}
      middleContent={middleContent}
      trailingContent={trailingContent}
    />
  )
}

// Assign static components to the main component
CantileverCard.Section = CantileverCardSection
CantileverCard.Group = CantileverCardGroup
CantileverCard.CardHeader = CantileverCardHeader
CantileverCard.SectionHeader = CantileverCardSectionHeader
CantileverCard.HiddenUnlessHovering =
  CantileverCardHiddenUnlessHoveringContainer

export default CantileverCard
